import { useEffect, useState } from "react";
import Nav from "../components/Nav";

const calendarApiUrl = process.env.REACT_APP_CALENDAR_API_URL;
const dayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const monthCount = 12;
const propertyOptions = [
  { id: "penthouse", name: "Penthouse" },
  { id: "main-floor", name: "Main Floor" },
  { id: "two-bedroom", name: "2 Bedroom" },
];

const isDateOnlyValue = (value) => /^\d{4}-\d{2}-\d{2}$/.test(value || "");

const parseCalendarDate = (value) => {
  if (!value) {
    return null;
  }

  if (isDateOnlyValue(value)) {
    const [year, month, day] = value.split("-").map(Number);
    return new Date(year, month - 1, day);
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed;
};

const startOfDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const addDays = (date, days) => {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
};

const addMonths = (date, months) =>
  new Date(date.getFullYear(), date.getMonth() + months, 1);

const formatDateKey = (date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDisplayEndDate = (event) => {
  const endDate = parseCalendarDate(event.end);

  if (!endDate) {
    return null;
  }

  if (isDateOnlyValue(event.start) && isDateOnlyValue(event.end)) {
    return addDays(endDate, -1);
  }

  return endDate;
};

const getUnavailableDateKeys = (events) => {
  const unavailableKeys = new Set();

  events.forEach((event) => {
    const startDate = parseCalendarDate(event.start);
    const endDate = parseCalendarDate(event.end);

    if (!startDate || !endDate) {
      return;
    }

    const inclusiveEndDate =
      isDateOnlyValue(event.start) && isDateOnlyValue(event.end)
        ? addDays(endDate, -1)
        : endDate;

    let currentDate = startOfDay(startDate);
    const lastDate = startOfDay(inclusiveEndDate);

    while (currentDate <= lastDate) {
      unavailableKeys.add(formatDateKey(currentDate));
      currentDate = addDays(currentDate, 1);
    }
  });

  return unavailableKeys;
};

const buildMonthDays = (monthDate) => {
  const firstDayOfMonth = new Date(
    monthDate.getFullYear(),
    monthDate.getMonth(),
    1
  );
  const lastDayOfMonth = new Date(
    monthDate.getFullYear(),
    monthDate.getMonth() + 1,
    0
  );
  const days = [];

  for (let index = 0; index < firstDayOfMonth.getDay(); index += 1) {
    days.push(null);
  }

  for (let day = 1; day <= lastDayOfMonth.getDate(); day += 1) {
    days.push(new Date(monthDate.getFullYear(), monthDate.getMonth(), day));
  }

  return days;
};

const formatDate = (value) => {
  if (!value) {
    return "Unknown";
  }

  const date = parseCalendarDate(value);

  if (!date) {
    return value;
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
};

const Calendar = () => {
  const [selectedProperty, setSelectedProperty] = useState(propertyOptions[0].id);
  const [calendarData, setCalendarData] = useState(null);
  const [loading, setLoading] = useState(Boolean(calendarApiUrl));
  const [error, setError] = useState("");

  useEffect(() => {
    if (!calendarApiUrl) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const loadCalendar = async () => {
      try {
        setLoading(true);
        setError("");
        setCalendarData(null);

        const requestUrl = new URL(calendarApiUrl);
        requestUrl.searchParams.set("property", selectedProperty);

        const response = await fetch(requestUrl.toString());

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const payload = await response.json();

        if (!cancelled) {
          setCalendarData(payload);
        }
      } catch (fetchError) {
        if (!cancelled) {
          setError(
            fetchError instanceof Error
              ? fetchError.message
              : "Unable to load calendar data"
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadCalendar();

    return () => {
      cancelled = true;
    };
  }, [selectedProperty]);

  const unavailableEvents =
    calendarData?.events?.filter((event) => event.isUnavailable) || [];
  const unavailableDateKeys = getUnavailableDateKeys(unavailableEvents);
  const today = new Date();
  const calendarStartDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    1
  );
  const visibleMonths = Array.from({ length: monthCount }, (_, index) =>
    addMonths(calendarStartDate, index)
  );

  return (
    <div className="min-h-screen bg-white text-left text-six">
      <Nav />

      <div className="px-6 pt-[160px] pb-12 sm:px-8 lg:px-16">
        <div className="mx-auto max-w-4xl rounded-sm border border-[#d9d4cb] bg-[#f7f4ee] p-8 shadow-sm lg:p-12">
          <div className="uppercase tracking-[0.35em] text-sm text-[#8a7c66]">
            Property Calendar
          </div>
          <h1 className="mt-4 text-4xl font-medium tracking-wide lg:text-5xl">
            Availability Calendar
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-8 text-[#5f5648]">
            This page displays the current booking blocks and available date ranges for the property.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            {propertyOptions.map((property) => {
              const isActive = property.id === selectedProperty;

              return (
                <button
                  key={property.id}
                  className={`rounded-sm border px-4 py-3 text-sm uppercase tracking-[0.25em] transition-colors ${
                    isActive
                      ? "border-[#7b5b2e] bg-[#7b5b2e] text-white"
                      : "border-[#d9d4cb] bg-white text-six"
                  }`}
                  onClick={() => setSelectedProperty(property.id)}
                  type="button"
                >
                  {property.name}
                </button>
              );
            })}
          </div>

          {!calendarApiUrl ? (
            <div className="mt-10 rounded-sm border border-dashed border-[#c5b9a5] bg-white p-6 text-[#5f5648] leading-8">
              Add `REACT_APP_CALENDAR_API_URL` to your local `.env` file and
              point it to the deployed Vercel API or a local `vercel dev`
              instance.
            </div>
          ) : loading ? (
            <div className="mt-10 rounded-sm border border-[#d9d4cb] bg-white p-6 text-[#5f5648]">
              Loading availability...
            </div>
          ) : error ? (
            <div className="mt-10 rounded-sm border border-[#d7b4a8] bg-[#fff8f5] p-6 text-[#8b4b3e] leading-8">
              Unable to load calendar data. {error}
            </div>
          ) : calendarData ? (
            <div className="mt-10 space-y-6">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-sm border border-[#d9d4cb] bg-white p-6">
                  <div className="text-sm uppercase tracking-[0.25em] text-[#8a7c66]">
                    Property
                  </div>
                  <div className="mt-3 text-2xl text-six">
                    {calendarData.property?.name || "Unknown"}
                  </div>
                </div>
                <div className="rounded-sm border border-[#d9d4cb] bg-white p-6">
                  <div className="text-sm uppercase tracking-[0.25em] text-[#8a7c66]">
                    Last Updated
                  </div>
                  <div className="mt-3 text-2xl text-six">
                    {formatDate(calendarData.fetchedAt)}
                  </div>
                </div>
              </div>

              <div className="rounded-sm border border-[#d9d4cb] bg-white p-6">
                <div className="flex flex-wrap items-center gap-4 text-sm uppercase tracking-[0.25em] text-[#8a7c66]">
                  <span>Calendar View</span>
                  <span className="flex items-center gap-2 normal-case tracking-normal text-[#5f5648]">
                    <span className="h-3 w-3 rounded-full bg-[#c79f61]"></span>
                    Unavailable
                  </span>
                  <span className="flex items-center gap-2 normal-case tracking-normal text-[#5f5648]">
                    <span className="h-3 w-3 rounded-full border border-[#d9d4cb] bg-white"></span>
                    Available
                  </span>
                </div>

                <div className="mt-6 grid gap-6 lg:grid-cols-2">
                  {visibleMonths.map((monthDate) => {
                    const monthDays = buildMonthDays(monthDate);

                    return (
                      <div
                        key={`${monthDate.getFullYear()}-${monthDate.getMonth()}`}
                        className="rounded-sm border border-[#ece4d8] bg-[#fcfaf6] p-4"
                      >
                        <div className="text-xl font-medium text-six">
                          {new Intl.DateTimeFormat("en-US", {
                            month: "long",
                            year: "numeric",
                          }).format(monthDate)}
                        </div>

                        <div className="mt-4 grid grid-cols-7 gap-2 text-center text-xs uppercase tracking-[0.2em] text-[#8a7c66]">
                          {dayLabels.map((label) => (
                            <div key={label}>{label}</div>
                          ))}
                        </div>

                        <div className="mt-3 grid grid-cols-7 gap-2 text-center text-sm">
                          {monthDays.map((day, index) => {
                            if (!day) {
                              return <div key={`empty-${index}`} className="h-10" />;
                            }

                            const dayKey = formatDateKey(day);
                            const isUnavailable = unavailableDateKeys.has(dayKey);

                            return (
                              <div
                                key={dayKey}
                                className={`flex h-10 items-center justify-center rounded-sm border ${
                                  isUnavailable
                                    ? "border-[#c79f61] bg-[#c79f61] text-white"
                                    : "border-[#e9e2d8] bg-white text-six"
                                }`}
                              >
                                {day.getDate()}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {unavailableEvents.length > 0 ? (
                <div className="rounded-sm border border-[#d9d4cb] bg-white p-6">
                  <div className="text-sm uppercase tracking-[0.25em] text-[#8a7c66]">
                    Booked Dates
                  </div>
                  <div className="mt-6 space-y-4">
                    {unavailableEvents.map((event) => (
                      <div
                        key={event.uid || `${event.start}-${event.end}`}
                        className="rounded-sm border border-[#ece4d8] bg-[#fcfaf6] p-4"
                      >
                        <div className="text-lg font-medium text-six">
                          {formatDate(event.start)} to {formatDate(
                            getDisplayEndDate(event)
                          )}
                        </div>
                        <div className="mt-2 text-[#5f5648]">
                          {event.summary || "Unavailable"}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="rounded-sm border border-[#d9d4cb] bg-white p-6 text-[#5f5648]">
                  No unavailable periods are currently returned by the calendar
                  API.
                </div>
              )}
            </div>
          ) : (
            <div className="mt-10 rounded-sm border border-dashed border-[#c5b9a5] bg-white p-6 text-[#5f5648]">
              Calendar data is not available yet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Calendar;